/**
 * PaletteBar — Color palette switcher.
 *
 * Renders 7 color swatches at the bottom of the document. Each swatch
 * represents a complete dark + light color system. Clicking a swatch
 * sets data-palette on <html>, which activates the corresponding CSS
 * custom property overrides.
 *
 * GATED: Only renders when VITE_SHOW_PALETTE=true in the environment.
 * In production builds without this var, the component returns null
 * and no palette UI is shipped.
 *
 * The light/dark toggle remains independent — you pick a palette
 * (e.g., "Copper") and toggle light/dark within it.
 */

import { useState, useEffect } from 'react';

/** Palette definitions — name, swatch color, data-palette value. */
const PALETTES = [
  { name: 'Emerald',  value: '',        dark: '#10b981', light: '#059669' },
  { name: 'Copper',   value: 'copper',  dark: '#d97706', light: '#b45309' },
  { name: 'Indigo',   value: 'indigo',  dark: '#6366f1', light: '#4f46e5' },
  { name: 'Rose',     value: 'rose',    dark: '#f43f5e', light: '#e11d48' },
  { name: 'Amber',    value: 'amber',   dark: '#f59e0b', light: '#d97706' },
  { name: 'Slate',    value: 'slate',   dark: '#94a3b8', light: '#64748b' },
  { name: 'Violet',   value: 'violet',  dark: '#a78bfa', light: '#7c3aed' },
] as const;

export default function PaletteBar() {
  const [active, setActive] = useState('');
  const [isLight, setIsLight] = useState(false);

  // Gate: only render if env var is set
  const showPalette = import.meta.env.VITE_SHOW_PALETTE === 'true';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setIsLight(document.documentElement.classList.contains('light'));

    // Restore saved palette
    const savedPalette = document.documentElement.getAttribute('data-palette') || '';
    setActive(savedPalette);

    // Watch for theme changes
    const observer = new MutationObserver(() => {
      setIsLight(document.documentElement.classList.contains('light'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (!showPalette) return null;

  const handleSelect = (value: string) => {
    setActive(value);
    if (typeof document === 'undefined') return;

    if (value) {
      document.documentElement.setAttribute('data-palette', value);
      localStorage.setItem('phackts-palette', value);
    } else {
      document.documentElement.removeAttribute('data-palette');
      localStorage.removeItem('phackts-palette');
    }
  };

  return (
    <div className="pt-8 border-t border-slate-900">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
        Color Palette
      </div>
      <div className="flex flex-wrap gap-3">
        {PALETTES.map((p) => {
          const isActive = active === p.value;
          const swatchColor = isLight ? p.light : p.dark;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => handleSelect(p.value)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-xs font-bold transition-all ${
                isActive
                  ? 'border-emerald-500/50 bg-slate-800/50 text-white shadow-lg'
                  : 'border-slate-800 bg-slate-900/50 text-slate-400 hover:border-slate-700 hover:text-slate-300'
              }`}
              aria-pressed={isActive}
              title={p.name}
            >
              <span
                className="w-4 h-4 rounded-full shrink-0 ring-2 ring-offset-1 ring-offset-slate-950 transition-shadow"
                style={{
                  backgroundColor: swatchColor,
                  ringColor: isActive ? swatchColor : 'transparent',
                  boxShadow: isActive ? `0 0 12px ${swatchColor}40` : 'none',
                }}
                aria-hidden="true"
              />
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
